import { type TFunction } from "i18next";
import {
  type WorkflowStepInstance,
  type WorkflowStepInstanceComment,
  type WorkflowStep,
  type WorkflowStepUserResponse,
  WorkflowStepUserResponses,
  WorkflowStepInstanceCommentTypes,
} from "~/api/types";
import { formatDate } from "./date";

/**
 * Get the latest instance for a step using the isLatestStepInstance flag from API.
 */
export function getLatestStepInstance(
  stepInstances: WorkflowStepInstance[],
  stepId: number,
): WorkflowStepInstance | undefined {
  return stepInstances.find(
    (i) => i.stepId === stepId && i.isLatestStepInstance,
  );
}

/**
 * Get all comments from the latest instance of a step.
 * Returns empty array if no latest instance exists.
 */
export function getStepComments(
  stepInstances: WorkflowStepInstance[],
  stepId: number,
) {
  const latestInstance = getLatestStepInstance(stepInstances, stepId);
  return latestInstance?.comments ?? [];
}

/**
 * Check if all current users have approved a step.
 */
export function allUsersApproved(step: WorkflowStep): boolean {
  if (step.users.length === 0) return false;

  return step.users.every(
    (user) => user.responseStatus === WorkflowStepUserResponses.approved,
  );
}

export function getCommentLabel(
  comment: WorkflowStepInstanceComment,
  t: TFunction,
): string {
  const date = formatDate(comment.createdAt, "dateTime");

  if (comment.type === WorkflowStepInstanceCommentTypes.approved)
    return `${t(($) => $.commons.workflow.approved)} - ${date}`;
  if (comment.type === WorkflowStepInstanceCommentTypes.rejected)
    return `${t(($) => $.commons.workflow.rejected)} - ${date}`;

  return `${t(($) => $.commons.workflow.freeComment)} - ${date}`;
}

export function getWorkflowStepTooltip(
  response: WorkflowStepUserResponse,
  user: { firstName: string; lastName?: string },
  isCurrentUser: boolean,
  t: TFunction,
): string {
  if (isCurrentUser) {
    switch (response) {
      case WorkflowStepUserResponses.approved:
        return t(($) => $.commons.workflows.stepUserResponses.approvedSelf);
      case WorkflowStepUserResponses.rejected:
        return t(($) => $.commons.workflows.stepUserResponses.rejectedSelf);
      default:
        return t(($) => $.commons.workflows.stepUserResponses.pendingSelf);
    }
  } else {
    const params = { firstName: user.firstName, lastName: user.lastName ?? "" };
    switch (response) {
      case WorkflowStepUserResponses.approved:
        return t(($) => $.commons.workflows.stepUserResponses.approved, params);
      case WorkflowStepUserResponses.rejected:
        return t(($) => $.commons.workflows.stepUserResponses.rejected, params);
      default:
        return t(($) => $.commons.workflows.stepUserResponses.pending, params);
    }
  }
}
